import {
  ResponsiveDialogBody,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHero,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { PLAN_VISUAL_CONFIG } from '@/lib/plans'
import { cn } from '@/lib/utils'

import locales from '../locales'

interface NoMatchStepProps {
  onBack: () => void
  /** Absent only if the catalog itself has no Enterprise SKU to point to. */
  onViewEnterprise?: () => void
}

/**
 * Reachable when the demand clears no SKU at or above the feature floor —
 * e.g. unlimited volume (or a large custom number) combined with API &
 * Webhooks: that floor is Business, and the only unlimited variant is
 * Starter. `getRecommendedSku` correctly returns null rather than matching
 * an undersized SKU (plan-finder.utils.ts), so step 4 needs its own state
 * for it instead of silently falling through to FeaturesStep with a runaway
 * "Step 4 of 3" counter. Mirrors ResultStep's visual language, but there is
 * no SKU to price here — only Enterprise to point to.
 */
export function NoMatchStep({ onBack, onViewEnterprise }: NoMatchStepProps) {
  const { t } = useComponentTranslation(locales)
  const visual = PLAN_VISUAL_CONFIG.Max
  const TierIcon = visual.icon

  return (
    <>
      <ResponsiveDialogBody layout="centered">
        <ResponsiveDialogHero
          icon={
            <div
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-xl',
                visual.iconBg,
              )}
            >
              <TierIcon className={cn('h-6 w-6', visual.iconColor)} />
            </div>
          }
        >
          <ResponsiveDialogTitle className="text-2xl">
            {t('result.noMatch.title')}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {t('result.noMatch.description')}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHero>
      </ResponsiveDialogBody>

      <ResponsiveDialogFooter>
        <Button variant="outline" onClick={onBack}>
          {t('actions.back')}
        </Button>
        {onViewEnterprise && (
          <Button onClick={onViewEnterprise}>{t('result.noMatch.cta')}</Button>
        )}
      </ResponsiveDialogFooter>
    </>
  )
}
